/**
 * AnyClip — 알리익스프레스 상품 카드 컴포넌트
 * 클릭 시 즉시 알리 상품페이지 오픈 + +1 티켓 지급
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useI18n } from '../context/I18nContext';
import './AliProductCard.css';

const BASE_URL = process.env.REACT_APP_API_URL || '';

// 안전한 URL 열기 — 반드시 유효한 URL 보장
function openUrl(url) {
  const safeUrl = url && url.startsWith('http') ? url : 'https://www.aliexpress.com/wholesale?SortType=bestmatch_sort';
  try {
    if (window.Telegram?.WebApp?.openLink) {
      window.Telegram.WebApp.openLink(safeUrl);
    } else {
      window.open(safeUrl, '_blank', 'noopener,noreferrer');
    }
  } catch (e) {
    window.open(safeUrl, '_blank', 'noopener,noreferrer');
  }
}

function AliProductCard({ type = 'mission1', telegramId, onTicketEarned, compact = false }) {
  const { t } = useI18n();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clicked, setClicked] = useState(false);
  const [limitReached, setLimitReached] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [type]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BASE_URL}/api/ali/products?type=${type}`);
      if (res.data.products?.length > 0) {
        setProduct(res.data.products[0]);
      }
    } catch (err) {
      console.error('[AliProduct] 조회 실패:', err.message);
      // 서버 오류 시 기본 상품 세팅
      setProduct({
        productId: 'fallback',
        title: t('ali_fallback_title'),
        price: t('ali_fallback_price'),
        originalPrice: '',
        discount: null,
        image: 'https://ae01.alicdn.com/kf/S9b8e6e7e7e3d4c7f8e7e7e7e7e7e7e7eL.jpg',
        url: `https://www.aliexpress.com/wholesale?SortType=bestmatch_sort&SearchText=${type === 'mission1' ? 'electronics' : 'home+decor'}`,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async () => {
    if (!product) return;

    // 1. 즉시 알리 상품 페이지 오픈
    openUrl(product.url);

    // 이미 클릭한 경우 티켓만 다시 요청하지 않음
    if (clicked) return;

    // 2. 티켓 지급 요청 (비동기, 실패해도 링크는 이미 열림)
    try {
      const res = await axios.post(`${BASE_URL}/api/ali/click`, {
        telegramId,
        productId: product.productId,
      });
      if (res.data.success) {
        setClicked(true);
        onTicketEarned && onTicketEarned(res.data.tickets);
      }
    } catch (err) {
      if (err.response?.data?.error === 'DAILY_LIMIT') {
        setLimitReached(true);  // 한도 초과 표시 (false success 제거)
      }
      // 티켓 오류여도 링크는 이미 열림 — 무시
    }
  };

  if (loading) {
    return (
      <div className={`ali-card ali-card--${compact ? 'compact' : 'full'} ali-loading`}>
        <div className="ali-skeleton" />
      </div>
    );
  }

  if (!product) return null;

  if (compact) {
    // 하단 배너용 콤팩트 카드 — 전체 클릭 가능
    return (
      <div className="ali-card ali-card--compact" onClick={handleClick} role="button" tabIndex={0}>
      <img src={product.image} alt={product.title} className="ali-img-compact"
          onError={e => { e.target.onerror=null; e.target.src='https://picsum.photos/seed/product/80/80'; }} />
        <div className="ali-info-compact">
          <span className="ali-price-compact">{product.price}</span>
          {product.discount && <span className="ali-discount-compact">{product.discount}</span>}
        </div>
      </div>
    );
  }

  // 미션용 풀 카드 — 카드 전체 + 버튼 모두 클릭 가능
  return (
    <div className="ali-card ali-card--full" onClick={handleClick} role="button" tabIndex={0} style={{ cursor: 'pointer' }}>
      <div className="ali-badge">{t('ali_badge_hotdeal')}</div>
      <img src={product.image} alt={product.title} className="ali-img-full"
        onError={e => { e.target.onerror=null; e.target.src='https://picsum.photos/seed/product/150/150'; }} />
      <div className="ali-info-full">
        <p className="ali-title">{product.title}</p>
        <div className="ali-price-row">
          <span className="ali-price">{product.price}</span>
          {product.originalPrice && product.originalPrice !== product.price && (
            <span className="ali-original">{product.originalPrice}</span>
          )}
          {product.discount && <span className="ali-discount">{product.discount}</span>}
        </div>
      </div>
      <div className={`ali-btn ${clicked ? 'ali-btn--done' : ''} ${limitReached ? 'ali-btn--limit' : ''}`}>
        {clicked ? t('ali_btn_done') : limitReached ? t('ali_btn_limit') : t('ali_btn_visit')}
      </div>
    </div>
  );
}

export default AliProductCard;
